import { createContext, useContext, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns3 } from 'lucide-react';
import { tableColumns, type SaveTablePreferenceInput, type TableKey } from '@tailoring-crm/shared';
import { Button } from './button.js';

/**
 * Where the picker reads and writes a person's column preference.
 *
 * The picker used to import the web app's react-query hooks directly. A shared UI package cannot
 * import application code, so the same three hooks are now handed in through context by whichever
 * app mounts the picker (the web app does it in `AppProviders`). Their shapes are exactly what the
 * app's `use-table-preferences` hooks already return, so nothing about how the picker behaves
 * changed — only where it finds them.
 */
export interface ColumnPickerPreferences {
  /** The columns one list shows, defaults included. Never `undefined`. */
  useTableColumns: (table: TableKey) => string[];
  useSaveTableColumns: () => {
    mutateAsync: (input: SaveTablePreferenceInput) => Promise<unknown>;
    isPending: boolean;
  };
  useResetTableColumns: () => {
    mutateAsync: (table: TableKey) => Promise<unknown>;
    isPending: boolean;
  };
}

const ColumnPickerPreferencesContext = createContext<ColumnPickerPreferences | null>(null);

export function ColumnPickerPreferencesProvider({
  preferences,
  children,
}: {
  preferences: ColumnPickerPreferences;
  children: ReactNode;
}): ReactElement {
  return (
    <ColumnPickerPreferencesContext.Provider value={preferences}>
      {children}
    </ColumnPickerPreferencesContext.Provider>
  );
}

function useColumnPickerPreferences(): ColumnPickerPreferences {
  const preferences = useContext(ColumnPickerPreferencesContext);
  if (!preferences) {
    throw new Error('ColumnPicker must be rendered inside a ColumnPickerPreferencesProvider');
  }
  return preferences;
}

/**
 * "Which columns do you want to see?" — one control, on any list.
 *
 * THE CHANGE IS SAVED IMMEDIATELY, with no Save button. A column picker with a form around it
 * makes somebody commit to a layout they cannot see yet; toggling one and watching the list
 * change underneath is the whole way this gets used.
 *
 * A column that cannot be hidden is shown, checked and DISABLED rather than left out. Leaving
 * it out would make somebody wonder where the name column went and whether they had lost it;
 * showing it greyed says plainly that it is always there.
 */
export function ColumnPicker({ table }: { table: TableKey }): ReactElement {
  const { t } = useTranslation();
  const { useTableColumns, useSaveTableColumns, useResetTableColumns } =
    useColumnPickerPreferences();
  const [open, setOpen] = useState(false);
  const columns = useTableColumns(table);
  const save = useSaveTableColumns();
  const reset = useResetTableColumns();

  const definitions = tableColumns(table);

  const toggle = (key: string): void => {
    const next = columns.includes(key)
      ? columns.filter((one) => one !== key)
      : // Appended in the registry's own order rather than at the end, so switching a column
        // back on puts it where it was rather than after everything else.
        definitions
          .filter((one) => one.key === key || columns.includes(one.key))
          .map((one) => one.key);
    void save.mutateAsync({ table, columns: next });
  };

  return (
    <div className="relative">
      <Button variant="outline" size="sm" aria-expanded={open} onClick={() => setOpen(!open)}>
        <Columns3 aria-hidden="true" />
        {t('tables.columns')}
      </Button>

      {open ? (
        <div
          role="group"
          aria-label={t('tables.columns')}
          className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-border bg-background p-2 shadow-lg"
        >
          <ul className="flex flex-col gap-0.5">
            {definitions.map((column) => (
              <li key={column.key}>
                <label className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent">
                  <input
                    type="checkbox"
                    checked={columns.includes(column.key)}
                    disabled={column.alwaysVisible === true || save.isPending}
                    onChange={() => toggle(column.key)}
                  />
                  {t(`tables.${table}.${column.key}`, column.defaultLabel)}
                </label>
              </li>
            ))}
          </ul>
          <div className="mt-1 border-t border-border pt-1">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              disabled={reset.isPending}
              onClick={() => void reset.mutateAsync(table)}
            >
              {t('tables.reset')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ColumnPicker;
